/**
 * Client for the activity routes of the fleet_state API (SW-8770).
 *
 * Same base URL and bearer as Tractor State's `configApi`: calls go straight
 * to `REACT_APP_FLEET_STATE_API_URL` with the caller's Cognito ACCESS token.
 */
import { urlFromEnv } from "../../utils/urlEnv";
import {
  CustomerSetting,
  CustomerSettings,
  FleetRequest,
  FleetResponse,
  RosterVehicle,
  Timeline,
  VehicleDays,
} from "./types";

const API_BASE = urlFromEnv(process.env.REACT_APP_FLEET_STATE_API_URL) ?? "";

export class ActivityApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "ActivityApiError";
    this.status = status;
  }
}

async function request<T>(
  path: string,
  token: string,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });
  if (res.ok) return (await res.json()) as T;
  let message = `Request failed (${res.status})`;
  try {
    const body = (await res.json()) as { error?: string };
    if (body?.error) message = body.error;
  } catch {
    // A gateway-level failure is not JSON; the status is the message.
  }
  throw new ActivityApiError(res.status, message);
}

/** The roster the caller may see, from the fleet_state read API. */
export async function fetchRoster(token: string): Promise<RosterVehicle[]> {
  const body = await request<{ vehicles: RosterVehicle[] }>(
    "/v1/vehicles",
    token,
  );
  return body.vehicles.filter((v) => v.status !== "decommissioned");
}

export function fetchTimeline(
  token: string,
  vehicle: string,
  date: string,
): Promise<Timeline> {
  const q = new URLSearchParams({ date });
  return request<Timeline>(
    `/v1/activity/vehicles/${encodeURIComponent(vehicle)}/timeline?${q}`,
    token,
  );
}

/**
 * The whole span in one call (SW-8879): every tractor the caller may see with
 * its readiness now, runs and one row per day, per-customer counts against
 * the threshold, and the fleet right now. Filters ride only when set.
 */
export function fetchFleet(
  token: string,
  q: FleetRequest,
): Promise<FleetResponse> {
  const params = new URLSearchParams({ from: q.from, to: q.to });
  if (q.customer) params.set("customer", q.customer);
  if (q.allocation) params.set("allocation", q.allocation);
  if (q.rigs) params.set("rigs", "true");
  return request<FleetResponse>(`/v1/activity/fleet?${params}`, token);
}

/** The daily rows of one tractor over a span (at most 92 days). */
export function fetchVehicleDays(
  token: string,
  vehicle: string,
  from: string,
  to: string,
): Promise<VehicleDays> {
  const q = new URLSearchParams({ from, to });
  return request<VehicleDays>(
    `/v1/activity/vehicles/${encodeURIComponent(vehicle)}?${q}`,
    token,
  );
}

/** Every customer's production-ready threshold (SW-8879). */
export const fetchCustomerSettings = (
  token: string,
): Promise<CustomerSettings> =>
  request<CustomerSettings>("/v1/settings/customers", token);

/** Set one customer's threshold; null clears it. Fleet-admin only. */
export function patchCustomerSetting(
  token: string,
  customer: string,
  minProductionReady: number | null,
): Promise<CustomerSetting> {
  return request<CustomerSetting>(
    `/v1/settings/customers/${encodeURIComponent(customer)}`,
    token,
    {
      method: "PATCH",
      body: JSON.stringify({ min_production_ready: minProductionReady }),
    },
  );
}
